// DOM chat overlay + status HUD. Deliberately plain DOM, not canvas: it
// stays crisp, accessible, and free.

import { CHAT_MAX_LENGTH } from "@bebop/shared";
import type { ChatEntry, ConnectionStatus } from "../core/world";

const STATUS_LABEL: Record<ConnectionStatus, string> = {
  connecting: "connecting…",
  connected: "live",
  reconnecting: "reconnecting…",
  failed: "connection lost — reload the page",
};

export class ChatOverlay {
  private log: HTMLDivElement;
  private input: HTMLInputElement;
  /** Whether the chat line itself currently holds the keyboard. */
  isFocused: () => boolean = () => false;
  private statusDot: HTMLSpanElement;
  private statusText: HTMLSpanElement;
  private nameText: HTMLSpanElement;
  private areaText: HTMLElement;

  constructor(
    root: HTMLElement,
    onSend: (text: string) => void,
    onFocusChange: (focused: boolean) => void,
  ) {
    root.insertAdjacentHTML(
      "beforeend",
      `
      <div class="hud">
        <span class="wordmark">BEBOP <em class="area-label"></em></span>
        <span class="status"><span class="dot"></span><span class="status-text"></span></span>
        <span class="self-name"></span>
      </div>
      <div class="chat">
        <div class="chat-log" aria-live="polite"></div>
        <input class="chat-input" type="text" maxlength="${CHAT_MAX_LENGTH}"
               placeholder="press Enter to chat" autocomplete="off" spellcheck="false" />
      </div>
      <div class="hint">WASD move · drag to look · Enter to chat · <a class="hint-link" href="/guide.html" target="_blank" rel="noopener">? guide</a></div>
      `,
    );

    this.log = root.querySelector(".chat-log") as HTMLDivElement;
    this.input = root.querySelector(".chat-input") as HTMLInputElement;
    this.statusDot = root.querySelector(".dot") as HTMLSpanElement;
    this.statusText = root.querySelector(".status-text") as HTMLSpanElement;
    this.nameText = root.querySelector(".self-name") as HTMLSpanElement;
    this.areaText = root.querySelector(".area-label") as HTMLElement;

    this.input.addEventListener("focus", () => onFocusChange(true));
    this.input.addEventListener("blur", () => onFocusChange(false));
    this.isFocused = () => document.activeElement === this.input;
    this.input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        const text = this.input.value.trim();
        if (text.length > 0) onSend(text);
        this.input.value = "";
        this.input.blur();
      } else if (e.key === "Escape") {
        this.input.blur();
      }
    });

    window.addEventListener("keydown", (e) => {
      // Enter opens chat only when nothing else is taking text — a board
      // panel's field keeps its own Enter (trades.md item 5).
      const el = document.activeElement;
      const typingElsewhere =
        !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
      if (e.key === "Enter" && !typingElsewhere) {
        e.preventDefault();
        this.input.focus();
      }
    });
  }

  addEntry(entry: ChatEntry): void {
    const div = document.createElement("div");
    div.className = entry.system ? "chat-entry system" : "chat-entry";
    if (entry.system) {
      div.textContent = entry.text;
    } else {
      const from = document.createElement("span");
      from.className = "from";
      from.textContent = entry.from;
      div.append(from, document.createTextNode(` ${entry.text}`));
    }
    this.log.append(div);
    while (this.log.childElementCount > 60) this.log.firstElementChild?.remove();
    this.log.scrollTop = this.log.scrollHeight;
  }

  setStatus(status: ConnectionStatus): void {
    this.statusDot.dataset.status = status;
    this.statusText.textContent = STATUS_LABEL[status];
  }

  setSelfName(name: string): void {
    this.nameText.textContent = name;
  }

  setAreaLabel(label: string): void {
    const next = `· ${label.toLowerCase()}`;
    if (this.areaText.textContent !== next) this.areaText.textContent = next;
  }
}
